import fp from 'lodash/fp';
import React, { useEffect, useMemo, useCallback } from 'react';
import { formatDate } from '@factorin/util';

import { connect } from 'Modules/core/redux';
import { Button, cn, Select2, CustomOption, Text } from 'Modules/ui';
import { translate, useTranslation } from 'Modules/core/i18n';

import { certificatesActions } from '../../state/certificates/actions';
import styles from './index.module.scss';


const Option = ({
    data: { data: { name, disabled, reason, validTo } },
    innerProps,
    innerRef,
    isSelected,
    isFocused,
}) => {
    const { t } = useTranslation('certificateSelector');
    return (
        <CustomOption
            isFocused={isFocused}
            isSelected={isSelected}
            ref={innerRef}
            className={cn(styles.Cert, disabled && styles.Cert_disabled)}
            {...innerProps}
        >
            <Text component='div' className={styles.Cert__name} textCompact>
                {name}
            </Text>
            {!disabled ? (
                <Text component='div' className={styles.Cert__date} textCompact>
                    {t('date', { date: formatDate(validTo) })}
                </Text>
            ) : (
                <Text component='div' className={styles.Cert__error} textCompact>
                    {t(`errors.${reason}`)}
                </Text>
            )}
        </CustomOption>
    );
};

const CertificateSelectorComponent = ({
    t, className,
    path, hideDisabled,
    selected, available, error,
    disabled, onChange, updateCertificateList,
    needUpdateButton = true,
}) => {
    const update = useCallback(
        () => {
            updateCertificateList(path, {});
        },
        [path, updateCertificateList],
    );
    useEffect(
        () => {
            update();
        },
        [update],
    );

    const options = useMemo(
        () => (available || []).map(
            data => ({
                data,
                value: data.thumbprint,
                label: data.name,
                disabled: data.disabled,
            }),
        ).filter(hideDisabled ? opt => !opt.disabled : fp.identity),
        [available, hideDisabled],
    );

    const change = useCallback(
        value => onChange(path, { cert: value }),
        [path, onChange],
    );
    const isCurrentItemDisable = useMemo(
        () => fp.some({ thumbprint: selected, disabled: true }, available),
        [selected, available],
    );
    useEffect(
        () => {
            if (isCurrentItemDisable)
                change(undefined);
        },
        [isCurrentItemDisable, change],
    );

    return (
        <div className={cn(styles.selector, className)}>
            {needUpdateButton && (
                <Button
                    data-testid='button-refresh'
                    variant='secondary'
                    className={styles.button}
                    disabled={disabled}
                    onClick={update}
                >
                    {t('refresh')}
                </Button>
            )}
            <Select2
                className={styles.select}
                variant='field'
                disabled={disabled}
                options={options}
                error={error}
                isOptionDisabled={option => option.disabled}
                value={selected}
                onChange={change}
                noOptionsMessage={t('placeholders.noResult')}
                placeholder={error
                    ? t('placeholders.error', { error })
                    : t('placeholders.normal')
                }
                components={{
                    Option,
                }}
            />
        </div>
    );
};

/** @type {React.FC<{ path: 'sign' | 'addToProfile', className?: string, hideDisabled?: boolean }>} */
export const CertificateSelector = fp.compose(
    translate('certificateSelector'),
    connect(
        ({ certificates }, { path }) => certificates[path],
        {
            updateCertificateList: certificatesActions.updateList,
            onChange: certificatesActions.setCertificate,
        },
    ),
)(CertificateSelectorComponent);
